import { Injectable, inject } from '@angular/core';
import { DateContextService } from '../../shared/services/date-context/date-context.service';
import {
  DateRangeService,
  QuickDatePreset,
} from '../../shared/services/date-presets/date-range.service';
import { TenantContextService } from '../tenant/tenant-context.service';
import { FilterConfig } from '../../shared/models/filter-config.model';

export interface DateRangeISO {
  start?: string;
  end?: string;
  compareStart?: string;
  compareEnd?: string;
}

export interface DateRangeLocal {
  start?: Date;
  end?: Date;
  compareStart?: Date;
  compareEnd?: Date;
}

/**
 * Compare-window mode. Controls how :class:`DateRangeDefaultsService`
 * derives the compare-period dates from the main date range.
 *
 *  - ``'prior-period'`` — equal-length window immediately preceding main
 *    (e.g. Q1 main → Q4 prior-year compare; rolling-90 main → previous
 *    90 days). The canonical default for every non-scorecard performance
 *    page in this app: the user expects "vs. previous quarter", not
 *    "vs. same quarter last year".
 *
 *  - ``'prior-year'`` — same window shifted back exactly one calendar
 *    year, clamped for leap-day edges. Used by the PE scorecard so its
 *    "Big-4 KPIs vs Prior Year" deltas are stable YoY across every
 *    quick-date preset, matching how PE sponsors reason about portco
 *    performance.
 *
 * Default across the codebase is ``'prior-period'``; scorecard opts into
 * ``'prior-year'`` explicitly via the ``compareMode`` input plumbed
 * through ``<app-page-scaffold>`` → ``<app-filter-bar>`` →
 * ``<app-quick-dates-dropdown>``.
 */
export type CompareMode = 'prior-period' | 'prior-year';

/**
 * Owns all date-range default calculation, formatting, and comparison logic.
 * Extracted from FilterFacade to satisfy Single Responsibility Principle.
 */
@Injectable({ providedIn: 'root' })
export class DateRangeDefaultsService {
  private dateContext = inject(DateContextService);
  private dateRangeService = inject(DateRangeService);
  private tenantContext = inject(TenantContextService);

  /**
   * Calculate default date ranges using "Last Quarter" preset.
   * Uses the last transaction date as reference when available, otherwise falls back to today.
   *
   * ``mode`` controls the compare-window shape — see {@link CompareMode}.
   * Defaults to ``'prior-period'`` (the immediately preceding quarter)
   * because that matches the intent of every performance page; the PE
   * scorecard opts into ``'prior-year'`` explicitly.
   */
  calculateDefaultDateRanges(mode: CompareMode = 'prior-period'): {
    start: Date;
    end: Date;
    compareStart: Date;
    compareEnd: Date;
  } {
    const tenantKey = this.tenantContext.currentTenantKey();
    const ltx = this.dateContext.lastTransactionDateFor(tenantKey);
    const referenceDate = ltx || new Date();

    const range = this.dateRangeService.compute('lastQuarter', referenceDate);
    // The default seed is always the "Last Quarter" preset — main aligns
    // exactly to a calendar quarter boundary. The user's mental model for
    // "previous quarter" is the full preceding calendar quarter (Q1 vs.
    // Q2, not "91 days back" which would clip into Q4 of the prior year),
    // so prior-period mode here delegates to the quarter-aligned helper.
    // Prior-year mode uses the generic year-shifted calculator since the
    // window length is preserved exactly.
    const { compareStart, compareEnd } =
      mode === 'prior-period'
        ? this.calculateCompareForLastQuarter(range.start)
        : this.calculateCompareDatesFromRange(range.start, range.end, mode);

    return { start: range.start, end: range.end, compareStart, compareEnd };
  }

  /**
   * Compute the full calendar quarter immediately preceding ``mainStart``.
   *
   * The "Last Quarter" preset's natural compare window is the prior
   * **calendar** quarter, not "the previous N days back from main start".
   * For Q2 2025 (Apr 1 – Jun 30) that means Q1 2025 (Jan 1 – Mar 31)
   * even though Q1 is 90 days and Q2 is 91. An equal-length backshift
   * would land at Dec 31 2024 → Mar 31 2025 — a window that crosses
   * the new-year boundary and confuses every PE / sales reader.
   */
  private calculateCompareForLastQuarter(mainStart: Date): {
    compareStart: Date;
    compareEnd: Date;
  } {
    const prevQuarterEnd = new Date(mainStart.getTime() - 24 * 60 * 60 * 1000);
    const month = prevQuarterEnd.getMonth();
    const quarterStartMonth = month < 3 ? 0 : month < 6 ? 3 : month < 9 ? 6 : 9;
    const compareStart = new Date(prevQuarterEnd.getFullYear(), quarterStartMonth, 1);
    const compareEnd = new Date(
      prevQuarterEnd.getFullYear(),
      prevQuarterEnd.getMonth(),
      prevQuarterEnd.getDate()
    );
    return { compareStart, compareEnd };
  }

  /**
   * Derive the compare window from arbitrary main boundaries per the
   * chosen {@link CompareMode}.
   *
   *  - ``'prior-period'`` (default) — equal-length window immediately
   *    preceding main. Compare ends the day before main starts and
   *    spans the same number of days as main. Used for custom date
   *    ranges and for non-quarter quick-date presets (last30, last90,
   *    lastMonth, ytd) where "previous N days" is the right semantic.
   *  - ``'prior-year'`` — same window shifted back exactly one year,
   *    clamped for leap-day edges (Feb 29 → Feb 28 in a non-leap year).
   *
   * Quarter-aligned defaults (``calculateDefaultDateRanges`` with the
   * Last Quarter preset) instead delegate to
   * {@link calculateCompareForLastQuarter} so the compare lands on the
   * full preceding calendar quarter rather than an equal-length tail.
   *
   * Both branches return inclusive boundary dates so consumers
   * serializing to ISO get the calendar-day a user would expect.
   */
  calculateCompareDatesFromRange(
    start: Date,
    end: Date,
    mode: CompareMode = 'prior-period'
  ): { compareStart: Date; compareEnd: Date } {
    if (mode === 'prior-year') {
      return {
        compareStart: this.shiftYearsClamped(start, -1),
        compareEnd: this.shiftYearsClamped(end, -1),
      };
    }
    // 'prior-period': equal-length window ending the day before main starts.
    const oneDayMs = 24 * 60 * 60 * 1000;
    const mainLengthDays = Math.max(
      1,
      Math.round((end.getTime() - start.getTime()) / oneDayMs) + 1
    );
    const compareEnd = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1);
    const compareStart = new Date(
      compareEnd.getFullYear(),
      compareEnd.getMonth(),
      compareEnd.getDate() - (mainLengthDays - 1)
    );
    return { compareStart, compareEnd };
  }

  private shiftYearsClamped(d: Date, yearOffset: number): Date {
    const targetYear = d.getFullYear() + yearOffset;
    const targetMonth = d.getMonth();
    const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    const targetDay = Math.min(d.getDate(), lastDayOfTargetMonth);
    return new Date(targetYear, targetMonth, targetDay);
  }

  /**
   * Canonical compare-window for a (preset, main-range, mode) triple.
   * Both the Quick Dates dropdown and FilterFacade reconciliation
   * delegate here. Prior-year shifts back one calendar year (leap-day
   * clamped). Prior-period: calendar-aligned for lastMonth /
   * thisQuarter / lastQuarter / lastYear; YoY for ytd; equal-length
   * backshift otherwise.
   */
  calculateCompareForPreset(
    preset: QuickDatePreset,
    mainStart: Date,
    mainEnd: Date,
    mode: CompareMode
  ): { compareStart: Date; compareEnd: Date } {
    if (mode === 'prior-year') {
      return {
        compareStart: this.shiftYearsClamped(mainStart, -1),
        compareEnd: this.shiftYearsClamped(mainEnd, -1),
      };
    }
    switch (preset) {
      case 'lastMonth':
      case 'mom':
        return this.prevFullMonthFromStart(mainStart);
      case 'thisQuarter':
      case 'lastQuarter':
        return this.prevFullQuarterFromStart(mainStart);
      case 'lastYear':
        return this.prevFullYearFromStart(mainStart);
      case 'ytd':
        return {
          compareStart: this.shiftYearsClamped(mainStart, -1),
          compareEnd: this.shiftYearsClamped(mainEnd, -1),
        };
      default:
        return this.calculateCompareDatesFromRange(mainStart, mainEnd, 'prior-period');
    }
  }

  /** Month index → starting month of its calendar quarter (0/3/6/9). */
  private quarterStartMonth(m: number): number {
    return m < 3 ? 0 : m < 6 ? 3 : m < 9 ? 6 : 9;
  }

  private prevFullMonthFromStart(start: Date): { compareStart: Date; compareEnd: Date } {
    const y = start.getFullYear();
    const m = start.getMonth();
    const pm = m === 0 ? 11 : m - 1;
    const py = m === 0 ? y - 1 : y;
    return {
      compareStart: new Date(py, pm, 1),
      compareEnd: new Date(py, pm + 1, 0),
    };
  }

  private prevFullQuarterFromStart(start: Date): { compareStart: Date; compareEnd: Date } {
    const qStartMonth = this.quarterStartMonth(start.getMonth());
    const pm = qStartMonth === 0 ? 9 : qStartMonth - 3;
    const py = qStartMonth === 0 ? start.getFullYear() - 1 : start.getFullYear();
    return {
      compareStart: new Date(py, pm, 1),
      compareEnd: new Date(py, pm + 3, 0),
    };
  }

  private prevFullYearFromStart(start: Date): { compareStart: Date; compareEnd: Date } {
    const y = start.getFullYear() - 1;
    return { compareStart: new Date(y, 0, 1), compareEnd: new Date(y, 11, 31) };
  }

  formatLocalDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  parseLocalDate(s?: string): Date | undefined {
    if (!s) return undefined;
    const parts = s.trim().split('-');
    if (parts.length !== 3) return undefined;
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return undefined;
    if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return undefined;
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d)
      return undefined;
    return date;
  }

  getEffectiveMaxDate(): Date {
    const today = new Date();
    const tenantKey = this.tenantContext.currentTenantKey();
    const ltx = this.dateContext.lastTransactionDateFor(tenantKey);
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (!ltx) return todayDate;
    return todayDate <= ltx ? todayDate : ltx;
  }

  buildDefaultFilterConfig(mode: CompareMode = 'prior-period'): FilterConfig {
    const { start, end, compareStart, compareEnd } = this.calculateDefaultDateRanges(mode);
    return {
      rangeDates: { start, end },
      compareRangeDates: { start: compareStart, end: compareEnd },
    };
  }

  buildFilterConfigFromDates(
    dateRangeLocal: DateRangeLocal,
    mode: CompareMode = 'prior-period'
  ): FilterConfig {
    const today = new Date();
    const start =
      dateRangeLocal.start || new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
    const end =
      dateRangeLocal.end || new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const { compareStart, compareEnd } = this.calculateCompareDatesFromRange(start, end, mode);
    const cs = dateRangeLocal.compareStart || compareStart;
    const ce = dateRangeLocal.compareEnd || compareEnd;

    return {
      rangeDates: { start, end },
      compareRangeDates: { start: cs, end: ce },
    };
  }
}
